import { Box, Button, Link, SpaceBetween } from '@amzn/awsui-components-react';
import { Event, EventStatus } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../../hooks/locale';
import { publishKatalMetric } from '../../katalAnalytics';
import { useSearchParams } from 'react-router-dom';

export interface EventListItemProps {
  event: Event;
  onDelete?: (eventId: string) => void;
}

export function EventListItem({ event, onDelete }: EventListItemProps) {
  const { t } = useTranslation(['activity', 'translation']);
  const locale = useLocale();
  const [searchParams] = useSearchParams();
  const urlFeaturesParam = searchParams.get('features');

  const isUnpublished = event.status === EventStatus.UNPUBLISHED;
  const eventHref = isUnpublished
    ? `/edit-event?id=${event.eventId}${urlFeaturesParam ? '&features=' + urlFeaturesParam : ''}`
    : `/create-event?draftId=${event.eventId}${urlFeaturesParam ? '&features=' + urlFeaturesParam : ''}`;

  const formatLastSaved = (date?: Date) => {
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return t('eventList.lastSavedJustNow');
    if (diffMinutes < 60) return t('eventList.lastSavedMinutesAgo', { count: diffMinutes });
    if (diffHours < 24) return t('eventList.lastSavedHoursAgo', { count: diffHours });
    if (diffDays === 1) {
      return t('eventList.lastSavedYesterday', {
        time: date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
      });
    }
    return t('eventList.lastSavedDate', {
      date: date.toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
    });
  };

  const metricName = isUnpublished ? 'editEvent_entryPoint' : 'createEvent_entryPoint';
  const onEntryPointClick = () => publishKatalMetric(metricName, 'draftTab');

  const eventTitle = event.title || t('eventList.untitledEvent');

  return (
    <Box
      padding={{ vertical: 's', horizontal: 'm' }}
      data-testid={`draft-event-item-${event.eventId}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box variant="div" float="left">
          <SpaceBetween direction="vertical" size="xxxs">
            <Link href={eventHref} fontSize="body-m" onFollow={onEntryPointClick}>
              {eventTitle}
            </Link>
            {event.updatedTime && (
              <Box variant="small" color="text-body-secondary">
                {formatLastSaved(event.updatedTime)}
              </Box>
            )}
          </SpaceBetween>
        </Box>
        <Box variant="div" float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              iconName="edit"
              variant="icon"
              ariaLabel={t('eventList.editAriaLabel', { title: eventTitle })}
              href={eventHref}
              onFollow={onEntryPointClick}
            />
            {onDelete && (
              <Button
                iconName="remove"
                variant="icon"
                ariaLabel={t('eventList.deleteAriaLabel', { title: eventTitle })}
                onClick={() => {
                  publishKatalMetric('createEvent_deleteEvent', event.eventId);
                  onDelete(event.eventId);
                }}
              />
            )}
          </SpaceBetween>
        </Box>
      </div>
    </Box>
  );
}
